import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  inject,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import {
  FMNTS_FORMS_OPTIONS,
  awaitControlValidation,
  isFormArray,
  isFormControl,
  isFormGroup,
} from '@fmnts/common/forms';
import { isNotNull } from '@fmnts/core';
import {
  ConditionDefinition,
  ConditionalService,
  ConditionalValidationService,
  ConditionalValidatorFn,
  DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN,
  DeclarativeFieldBuilder,
  DeclarativeForm,
  FieldDefinition,
  FormDeclaration,
  GraphBuilder,
  PageDefinition,
  TemplateBuilder,
  ValidationBuilder,
  isConditionalValidatorFn,
  isFieldDeclaration,
  isFieldsetDeclaration,
} from '@fmnts/declarative-forms/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  concat,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  of,
  startWith,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { DeclarativeFieldComponent } from './declarative-field.component';

/**
 * Describes the behaviour of the form validation
 * Default Strategey is `onSubmit`
 */
export enum ValidationStrategy {
  /**
   * Will enable instant validation
   */
  OnChange = 'onChange',
  /**
   * Will validate when `submit$` is called.
   * This is the default.
   */
  OnSubmit = 'onSubmit',
  /**
   * Will not validate the form.
   */
  None = 'none',
}

interface ValidatePageOptions {
  /**
   * Will call `updateValueAndValidity` on all page controls
   */
  updateValue?: boolean;
  /**
   * Will call `markAllAsTouched` on all page controls
   */
  markAsTouched?: boolean;
}

/**
 * @param page Page for which control paths should be returned
 *
 * @returns
 * A structure with the paths to all
 * * fieldset controls
 * * field controls
 *
 * for this page.
 */
function getControlPathsForPage(page: PageDefinition) {
  interface ControlPaths {
    fieldsets: [string][];
    fields: [string, string][];
  }

  return page.fieldsets.reduce<ControlPaths>(
    (acc, fs) => ({
      fieldsets: [...acc.fieldsets, [fs.key]],
      fields: [
        ...acc.fields,
        ...fs.fields.map<[string, string]>((f) => [fs.key, f.key]),
      ],
    }),
    { fieldsets: [], fields: [] },
  );
}

@Component({
  selector: 'declarative-form',
  templateUrl: './declarative-form.component.html',
  providers: [GraphBuilder, TemplateBuilder, ValidationBuilder],
  standalone: false,
})
export class DeclarativeFormComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private readonly cfg = inject(FMNTS_FORMS_OPTIONS);
  readonly #focusMonitor = inject(FocusMonitor);
  readonly #element = inject(ElementRef);
  readonly #destroyRef = inject(DestroyRef);

  @Input() page!: PageDefinition;
  @Input() pages!: PageDefinition[];
  @Input() validationStrategy: ValidationStrategy = ValidationStrategy.OnSubmit;

  /** Whether browser autocomplete should be enabled on the form. */
  readonly autocomplete = input<boolean>(this.cfg.autocomplete ?? false);

  readonly focusFirstElement = computed(
    () => this.cfg.autofocusFirstElement ?? false,
  );
  /** Whether initial focus was applied. */
  readonly #focusedApplied = signal<boolean>(false);
  /** Whether any field can be autofocused. */
  readonly canAutofocus = computed(
    () => this.focusFirstElement() && !this.#focusedApplied(),
  );

  private readonly fields = viewChildren<DeclarativeFieldComponent>(
    DeclarativeFieldComponent,
  );

  /**
   * Emits when the validation result changes.
   * Only emits when `ValidationStrategy` is set to `OnChange`.
   */
  @Output() isValidChanged = new EventEmitter<boolean>(true);

  /**
   * Emits when the form was resetted.
   */
  @Output() readonly formReset = new EventEmitter<void>();

  private destroyed$ = new ReplaySubject<boolean>(1);
  private _declaration$ = new BehaviorSubject<FormDeclaration | null>(null);
  /** Observable that emits when the declaration changes */
  readonly declaration$ = this._declaration$.pipe(filter(isNotNull));

  private readonly _form: DeclarativeForm = new DeclarativeForm({});

  public get declaration(): FormDeclaration {
    const value = this._declaration$.getValue();
    if (!value) {
      throw new Error('Failed to fetch declaration');
    }
    return value;
  }

  public get form(): DeclarativeForm {
    return this._form;
  }

  public control(path: string[]): AbstractControl<any, any> {
    const control = this.form.get(path);
    if (!control) {
      throw new Error(`Failed to fetch Control for path: ${path.join('.')}`);
    }
    return control;
  }

  constructor(
    private validationBuilder: ValidationBuilder,
    private conditionalService: ConditionalService,
    private conditionalValidatorService: ConditionalValidationService,
    @Inject(DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN)
    private fieldBuilder: DeclarativeFieldBuilder<FieldDefinition>,
  ) {}

  ngOnInit(): void {
    // Watch conditions for fields and fieldsets for all pages
    this.declaration$
      .pipe(
        filter(isNotNull),
        switchMap((declaration) =>
          merge(
            ...declaration.allFieldsets.map((fs) =>
              this.watchControlCondition([fs.key]),
            ),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    if (this.validationStrategy !== ValidationStrategy.OnChange) {
      // No more initialization needed
      return;
    }

    this.form.statusChanges
      .pipe(
        // Validate upon initialization
        startWith(this.form.status),
        map((status) => status === 'VALID'),
        distinctUntilChanged(),
        takeUntil(this.destroyed$.asObservable()),
      )
      .subscribe((isValid) => {
        this.isValidChanged.emit(isValid);
      });
  }

  ngAfterViewInit(): void {
    this.#setupFocusMonitoring();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const pagesChange = changes['pages'];
    if (pagesChange) {
      if (pagesChange && pagesChange.currentValue) {
        this.initializeForm(pagesChange.currentValue as PageDefinition[]);
      }
    }
  }

  /**
   * Resets the values for this form
   */
  public resetForm(values: Record<string, unknown>): void {
    this.form.reset(values);
    this.formReset.emit();
    this.resetFocus();
  }

  /**
   * Reset the focus for the first input field
   */
  private resetFocus(): void {
    if (this.fields().length) {
      this.fields()[0].resetFocus();
    }
    this.#focusedApplied.set(false);
  }

  /**
   * Validates the controls on the given `page` and awaits async validation.
   * Use `opts` to control the desired behavior for validation.
   *
   * @param page Page that should be validated
   * @param opts Options for page validation
   *
   * @returns
   * An `Observable` that - once validation finished - emits
   * with `true` if the form is valid, then completes.
   */
  private validatePage$(
    page: PageDefinition,
    opts: ValidatePageOptions = {},
  ): Observable<boolean> {
    const controlPaths = getControlPathsForPage(page);

    controlPaths.fields.forEach((path) => {
      const control = this.control(path);

      if (opts.markAsTouched) {
        // Maybe we should only do this if enabled
        control.markAllAsTouched();
      }

      if (opts.updateValue) {
        control.updateValueAndValidity();
      }

      // This is only for logging
      if (control.pending) {
        console.warn('validation pending for', path.join('.'));
      }
    });

    return awaitControlValidation(this.form).pipe(
      map((status) => status === 'VALID'),
      map((formIsValid) => {
        if (formIsValid) {
          // Everything's valid, we can proceed
          return true;
        }

        // Let's find out if the invalid control is on the given page.
        const allPaths = [...controlPaths.fieldsets, ...controlPaths.fields];

        // Something like `controlPaths.fieldsets.every(...)` would be sufficient
        // here, but we want to log every enabled, non-valid control
        const nonValidControls = allPaths.filter((p) => {
          const control = this.control(p);
          if (control.valid || control.disabled) {
            return false;
          }

          console.warn(`validation result for ${p.join('.')}`, control.status);
          return true;
        });

        return nonValidControls.length === 0;
      }),
    );
  }

  /**
   * Validates the current form page and awaits async validations
   * Performs the following for each control on the current form page:
   * * Mark as touched
   * * update value and validity
   *
   * @returns
   * An `Observable` that - once validation finished - emits
   * with `true` if the form is valid, then completes.
   */
  public validateFormPage$(): Observable<boolean> {
    return this.validatePage$(this.page, {
      markAsTouched: true,
      updateValue: true,
    });
  }

  /**
   * Validates the form and awaits async validations
   *
   * @returns
   * An `Observable` that - once validation finished - emits
   * with `true` if the form is valid, then completes.
   */
  public validate$(): Observable<boolean> {
    return this.validateFormPage$().pipe(
      switchMap((formPageValid) =>
        formPageValid
          ? awaitControlValidation(this.form).pipe(
              map((status) => status === 'VALID'),
            )
          : of(false),
      ),
    );
  }

  /**
   * @returns
   * An observable that emits with the flattened form value
   * or with an error.
   *
   * @description
   * ## ValidationStrategy
   * - `OnSubmit` & `OnChange`:
   * will return the form if it is valid.
   * otherwise it will return an error
   *
   * - `None`:
   * will return the form value without validation
   *
   */
  public submit$(): Observable<Record<string, unknown>> {
    if (this.validationStrategy === ValidationStrategy.None) {
      return of(this.form.flatValue);
    }

    return this.validate$().pipe(
      map((isValid) => {
        if (isValid) {
          return this.form.flatValue;
        }
        throw new Error('form is invalid');
      }),
    );
  }

  /**
   * @return form value (returned without validation)
   */
  public getFormValue(): Record<string, unknown> {
    return this.form.flatValue;
  }

  /**
   * Initializes or updates the form from the given `pages`.
   * Should be called, when the pages have changed.
   *
   * @param pages Form pages
   */
  private initializeForm(pages: readonly PageDefinition[]) {
    const declaration = new FormDeclaration(pages);
    this._updateFormFromDeclaration(declaration);
    this._declaration$.next(declaration);
  }

  #setupFocusMonitoring() {
    this.#focusMonitor.monitor(this.#element, true).subscribe((origin) => {
      if (origin !== null) {
        this.#focusedApplied.set(true);
      }
    });

    this.#destroyRef.onDestroy(() => {
      this.#focusMonitor.stopMonitoring(this.#element);
    });
  }

  /**
   * Observes the condition for the control (field or fieldset) at
   * the given `path` and will automatically `enable` or `disable`
   * the control. It will also do so for any child controls.
   *
   * @param path Path to the field / fieldset
   *
   * @returns
   * An `Observable` that will emit with `[path, fulfilled]`,
   * where `path` contains the path to the control and `fulfilled`
   * is a boolean that is `true` if the condition was fulfilled.
   */
  private watchControlCondition(
    path: string[],
  ): Observable<[string[], boolean]> {
    const control = this.control(path);
    const field = this._declaration$.getValue()?.get(path);

    if (
      !field ||
      (!isFieldDeclaration(field) && !isFieldsetDeclaration(field))
    ) {
      console.error(
        `Can't watch control condition. Could not resolve path '${path.join(
          '.',
        )}' to field or fieldset.`,
      );
      return EMPTY;
    }

    return this.fulfillsCondition$(field.condition).pipe(
      // Enable/disable the observed control here before continue
      // observing any child controls. This is due to how Angular
      // handles the `disabled` state for `FormGroup`s, which will
      // be disabled, if all children are disabled. This might have
      // effect on validations on fieldsets, but right now there
      // is no use case for this.
      tap((fulfilled) => {
        if (fulfilled) {
          if (!control.enabled) {
            control.enable();
          }
        } else {
          if (!control.disabled) {
            control.disable();
          }
        }
      }),
      // Pass on the fulfilled result from the current control
      // In case we're dealing with any aggregated control
      // (e.g. `FormGroup` or `FormArray`), also setup watching
      // children. Use `switchMap` to unsubscribe from child updates
      // when parent does no longer fulfill its own condition.
      switchMap((fulfilled) => {
        const initial$ = of([path, fulfilled] as [string[], boolean]);
        return !fulfilled || isFormControl(control)
          ? initial$
          : concat(initial$, this.watchChildrenConditions(control, path));
      }),
    );
  }

  /**
   * Sets up the `Observable` for watching conditions on child
   * controls for any aggregated `AbstractControl`. Currently
   * this supports `FormGroup` and `FormArray`
   *
   * @param parent Parent control
   * @param parentPath Path to the parent control
   *
   * @returns
   * `Observable` that emits whenever the `fulfilled` state of
   * a child changes.
   */
  private watchChildrenConditions(
    parent: AbstractControl,
    parentPath: string[],
  ) {
    return isFormGroup(parent)
      ? merge(
          ...Object.keys(parent.controls).map((name) =>
            this.watchControlCondition([...parentPath, name]),
          ),
        )
      : isFormArray(parent)
        ? merge(
            ...parent.controls.map((_, name) =>
              this.watchControlCondition([...parentPath, `${name}`]),
            ),
          )
        : throwError(() => new Error('Unknown AbstractControl'));
  }

  /**
   * Creates an `Observable` that will emit `true` if the condition
   * for is fulfilled. It will emit immediately.
   *
   * @param condition Conditition to evaluate
   *
   * @returns
   * `Observable` that emits with a boolean value that is
   * `true` if the condition is fulfilled and `false` if
   * the condition is no longer fulfilled.
   */
  private fulfillsCondition$(condition?: ConditionDefinition) {
    if (!condition) {
      return of(true);
    }

    return this.form.flatValueChanges.pipe(
      // Emit right away with the current value
      startWith<Record<string, unknown>>(this.form.flatValue),
      // Maps this to the conditions fulfilled state
      this.conditionalService.fulfills$(condition),
      distinctUntilChanged(),
    );
  }

  private _updateFormFromDeclaration(declaration: FormDeclaration) {
    declaration.allFieldsets.forEach((fs) => {
      const existingFieldsetGroup = this.form.controls[
        fs.key
      ] as UntypedFormGroup;
      if (existingFieldsetGroup) {
        // Fieldset group already exists
        fs.fields.forEach((field) => {
          // Field does not exists yet
          if (!existingFieldsetGroup.controls[field.key]) {
            const fieldControl = this.fieldToFormControl(declaration, [
              fs.key,
              field.key,
            ]);
            existingFieldsetGroup.addControl(field.key, fieldControl);
            if (fieldControl.dirty) {
              existingFieldsetGroup.markAsDirty();
            }
          }
        });
      } else {
        // Fieldset group does not exist yet
        const fieldsetFormGroup = this.fieldsetToFormGroup(declaration, [
          fs.key,
        ]);
        this.form.addControl(fs.key, fieldsetFormGroup);
        if (fieldsetFormGroup.dirty) {
          this.form.markAsDirty();
        }
      }
    });
  }

  private fieldsetToFormGroup(declaration: FormDeclaration, path: [string]) {
    const fieldset = declaration.get(path);

    if (!fieldset) {
      throw new Error(`Could not find fieldset for path: [${path.join('.')}]`);
    }

    const initialState = {
      dirty: false,
    };

    const createField = (fieldPath: [string, string]) => {
      const ctrl = this.fieldToFormControl(declaration, fieldPath);
      if (ctrl.dirty) {
        initialState.dirty = true;
      }
      return ctrl;
    };
    const fieldsetControls = fieldset.fields.reduce(
      (acc, cur) => ({
        ...acc,
        [cur.key]: createField([fieldset.key, cur.key]),
      }),
      {},
    );

    const group = new UntypedFormGroup(fieldsetControls);
    if (initialState.dirty) {
      group.markAsDirty();
    }

    return group;
  }

  private fieldToFormControl(
    declaration: FormDeclaration,
    path: [string, string],
  ): UntypedFormControl {
    // Get the initial state of the control
    const field = declaration.get(path);
    if (!field) {
      throw new Error(
        `Could not initialize control. Could not find field for path: [${path.join(
          '.',
        )}]`,
      );
    }
    const initialFieldState = this.fieldBuilder.getInitialFieldControlState(
      field,
      path,
    );

    // If `ValidationStrategy.None` is set, don't add validators to control
    if (this.validationStrategy === ValidationStrategy.None) {
      return new UntypedFormControl(initialFieldState.state, {
        ...initialFieldState.options,
      });
    }

    // Prepare validators
    const { validators = [], asyncValidators = [] } =
      this.validationBuilder.buildValidations({
        field,
        declaration,
      });

    this.fieldNeedsRevalidation$(validators).subscribe(() => {
      const control = this.form.get(path);
      if (control) {
        control.updateValueAndValidity();
      }
    });

    // Create the form control for the field and update UI state
    const fieldControl = new UntypedFormControl(initialFieldState.state, {
      ...initialFieldState.options,
      validators,
      asyncValidators,
    });

    if (initialFieldState.uiState.dirty) {
      fieldControl.markAsDirty();
    }

    return fieldControl;
  }

  /**
   * Emits whenever a validator must be re-validated.
   *
   * @param validators List of validators
   *
   * @returns
   * Observable that emits when the control should be re-validated.
   */
  private fieldNeedsRevalidation$(
    validators: (ValidatorFn | ConditionalValidatorFn)[],
  ): Observable<unknown> {
    const conditionalValidators = validators.filter(isConditionalValidatorFn);

    // Start observing conditional validators, only if there are any
    if (conditionalValidators.length === 0) {
      return EMPTY;
    }

    return this.form.valueChanges.pipe(
      // For heavy automatic changes on the form,
      // recalculating the value and validity is
      // debounced in order to operate on a "settled" value
      debounceTime(100),
      this.conditionalValidatorService.needsUpdate$(conditionalValidators),
      takeUntil(this.destroyed$),
    );
  }
}
